import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useSnackbar } from 'notistack';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Grid from '@material-ui/core/Grid';
import InvoiceCommunication from './InvoiceCommunication';
import InvoiceDetails from './InvoiceDetails';
import InvoiceItems from './InvoiceItems';
import InvoiceLogs from './InvoiceLogs';
import InvoiceProvider from '../../components/InvoiceProvider';
import PaymentStatusDrawer from '../../components/PaymentStatusDrawer';
import DataFlowDrawer from '@afosto/app-components-react/DataFlowDrawer';
import Page from '@afosto/app-components-react/Page';
import ProgressDialog from '@afosto/app-components-react/ProgressDialog';
import SkeletonPage from '@afosto/app-components-react/SkeletonPage';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import { addresses as addressesApi } from '@afosto/contacts/api/Addresses';
import { calculations as calculationsApi } from '@afosto/order/api/Calculations';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { invoices as invoicesApi } from '@afosto/order/api/Invoices';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import translations from './translations';

const InvoiceView = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['odr:invoices:read']);

  const history = useHistory();
  const intl = useIntl();
  const location = useLocation();
  const { id: invoiceID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [invoice, setInvoice] = useState(null);
  const [isFetchingInvoice, setIsFetchingInvoice] = useState(true);
  const [isFetchingAddress, setIsFetchingAddress] = useState(true);
  const [calculation, setCalculation] = useState(null);
  const [client, setClient] = useState(null);
  const [billingAddress, setBillingAddress] = useState(null);
  const [contact, setContact] = useState(null);
  const [isFetchingContact, setIsFetchingContact] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [organisation, setOrganisation] = useState(null);
  const [isFetchingOrganisation, setIsFetchingOrganisation] = useState(true);
  const [showChangePaymentStatus, setShowChangePaymentStatus] = useState(false);
  const [showDataFlowDrawer, setShowDataFlowDrawer] = useState(false);

  /**
   * Change the payment status of the invoice.
   * @param {Date} paidAt
   * @returns {Promise<void>}
   */
  const handleChangePaymentStatus = paidAt => {
    setShowChangePaymentStatus(false);
    setInvoice({ ...invoice, is_paid: true, paid_at: paidAt });
  };

  const handleTriggerDataFlow = () => setShowDataFlowDrawer(true);

  const handleGeneratePdf = async () => {
    try {
      setIsGeneratingPdf(true);

      const response = await invoicesApi.generatePdfInvoice(invoiceID);
      const { headers = {} } = response ?? {};
      const { 'x-task-id': taskID = null } = headers;
      const { result } = await scheduleTask(taskID);

      if (result) {
        const file = JSON.parse(result);
        setInvoice({ ...invoice, file_id: file?.id });
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.invoiceGeneratingPdfFailed), {
        variant: 'error',
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchInvoice = async id => {
      setIsFetchingInvoice(true);

      try {
        const response = await invoicesApi.viewInvoice(id);
        const { data = null } = response;

        if (isMounted) {
          setInvoice(data);
        }
      } catch (error) {
        if (isMounted) {
          setInvoice(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingInvoice(false);
        }
      }
    };

    if (invoiceID) {
      fetchInvoice(invoiceID);
    }

    return () => {
      isMounted = false;
    };
  }, [invoiceID]);

  useEffect(() => {
    let isMounted = true;

    const fetchCalculation = async calculationId => {
      try {
        if (!calculationId) {
          return;
        }

        const response = await calculationsApi.getCalculation(calculationId);
        const { data = null } = response ?? {};

        if (isMounted) {
          setCalculation(data);
        }
      } catch (err) {
        if (isMounted) {
          setCalculation(null);
        }
      }
    };

    const fetchClient = async clientId => {
      try {
        const response = await oauthApi.getClient(clientId);
        const { data = null } = response;

        if (isMounted) {
          setClient(data);
        }
      } catch (err) {
        if (isMounted) {
          setClient(null);
        }
      }
    };

    const fetchContact = async contactID => {
      setIsFetchingContact(true);

      try {
        if (!contactID) {
          setIsFetchingContact(false);
          return;
        }

        const response = await contactsApi.getContact(contactID);
        const { data = {} } = response;

        if (isMounted) {
          setContact(data);
        }
      } catch (error) {
        if (isMounted) {
          setContact(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingContact(false);
        }
      }
    };

    const fetchOrganisation = async organisationID => {
      setIsFetchingOrganisation(true);

      try {
        if (!organisationID) {
          setIsFetchingOrganisation(false);
          return;
        }

        const response = await organisationsApi.viewOrganisation(organisationID);
        const { data } = response;

        if (isMounted) {
          setOrganisation(data);
        }
      } catch (error) {
        if (isMounted) {
          setOrganisation(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingOrganisation(false);
        }
      }
    };

    const fetchAddresses = async billingAddressID => {
      setIsFetchingAddress(true);

      try {
        const billingAddressResponse = await addressesApi.viewAddress(billingAddressID);
        const { data: billingAddressData } = billingAddressResponse;

        if (isMounted) {
          setBillingAddress(billingAddressData);
        }
      } catch (err) {
        if (isMounted) {
          setBillingAddress(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingAddress(false);
        }
      }
    };

    if (invoice) {
      const { customer } = invoice;

      if (customer?.contact) {
        fetchContact(customer.contact.id);

        if (customer.contact.address_id) {
          fetchAddresses(customer.contact.address_id);
        } else {
          setIsFetchingAddress(false);
        }

        if (customer.contact.organisation_id) {
          fetchOrganisation(customer.contact.organisation_id);
        } else {
          setIsFetchingOrganisation(false);
        }
      } else {
        setIsFetchingContact(false);
        setIsFetchingAddress(false);
        setIsFetchingOrganisation(false);
      }

      fetchCalculation(invoice?.calculation_id);
      fetchClient(invoice?.client_id);
    }

    return () => {
      isMounted = false;
    };
  }, [invoice]);

  if (!hasPermissions()) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <UnauthorizedComponent />
      </SkeletonPage>
    );
  }

  if (isFetchingInvoice) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8} xl={9}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader title={<SkeletonBody lines={1} />} />
                  <CardContent>
                    <SkeletonBody lines={3} />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </SkeletonPage>
    );
  }

  const secondaryActions = [
    ...(!invoice?.is_paid && hasPermissions(['odr:invoices:update'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.changePaymentStatus),
            action: () => setShowChangePaymentStatus(true),
          },
        ]
      : []),
    {
      text: intl.formatMessage(translations.secondaryActions.triggerDataFlow),
      action: handleTriggerDataFlow,
    },
    {
      text: intl.formatMessage(translations.secondaryActions.generatePdf),
      action: handleGeneratePdf,
    },
  ];

  if (!invoice) {
    return (
      <SkeletonPage fullWidth breadcrumbs={2} subNavigation={3}>
        Invoice not found
      </SkeletonPage>
    );
  }

  return (
    <InvoiceProvider
      value={{
        calculation,
        invoice,
        isFetchingContact,
        contact,
        client,
        organisation,
        isFetchingOrganisation,
        billingAddress,
        isFetchingAddress,
      }}
    >
      <Page
        title={invoice?.number ?? 'Concept'}
        fullWidth
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/invoices/${invoiceID}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.items),
              path: `/invoices/${invoiceID}/items`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.communication),
              path: `/invoices/${invoiceID}/communication`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/invoices/${invoiceID}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.invoices),
            path: '/invoices',
          },
          {
            label: invoice?.number ?? 'Concept',
            path: `/invoices/${invoiceID}`,
            active: true,
          },
        ]}
        linkComponent={Link}
        secondaryActions={secondaryActions.length > 0 ? secondaryActions : null}
      >
        <Switch>
          <Route component={InvoiceItems} path="/invoices/:id/items" />
          <Route component={InvoiceLogs} path="/invoices/:id/logs" />
          <Route component={InvoiceCommunication} path="/invoices/:id/communication" />
          <Route component={InvoiceDetails} path="/invoices/:id" />
        </Switch>
      </Page>
      <PaymentStatusDrawer
        invoice={invoice}
        isOpen={showChangePaymentStatus}
        onClose={() => setShowChangePaymentStatus(false)}
        onChange={handleChangePaymentStatus}
      />
      <DataFlowDrawer
        open={showDataFlowDrawer}
        filter="invoice"
        onClose={() => setShowDataFlowDrawer(false)}
        onFinish={() => setShowDataFlowDrawer(false)}
        entityId={invoiceID}
      />
      <ProgressDialog
        isOpen={isGeneratingPdf}
        message={intl.formatMessage(translations.generatePdfProgressDialog.message)}
      />
    </InvoiceProvider>
  );
};

export default InvoiceView;
